import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from '@/components/ui/toaster'
import { Toaster as Sonner } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { AuthProvider } from './contexts/AuthContext'
import { ProtectedRoute, TeamProtectedRoute } from './components/ProtectedRoute'

// Authentication components
import { LoginForm } from './components/Auth/LoginForm'
import { SignupForm } from './components/Auth/SignupForm'

// Dashboard components
import { DashboardHome } from './components/DashboardHome'
import { PricingPage } from './components/PricingPage'
import { RootRedirect } from './components/RootRedirect'

// New UI components
import Index from './pages/Index'

// Legacy components (will be updated or removed)
import Dashboard from './pages/Dashboard'
import TeamDashboard from './pages/TeamDashboard'
import InstallSuccess from './pages/InstallSuccess'
import OAuthTest from './pages/OAuthTest'
import VoiceRecord from './pages/VoiceRecord'
import SlackOAuthCallback from './pages/SlackOAuthCallback'
import OAuthConsent from './pages/OAuthConsent'
import InstallClaudeCode from './pages/InstallClaudeCode'
import TeamAgents from './pages/TeamAgents'
import AgentDetail from './pages/AgentDetail'
import UserStandupHistory from './pages/UserStandupHistory'
import TeamCheckins from './pages/TeamCheckins'
import TeamStandupSummary from './pages/TeamStandupSummary'
import TeamManagement from './pages/TeamManagement'
import Projects from './pages/Projects'
import ProjectDetails from './pages/ProjectDetails'
import TeamConfig from './pages/TeamConfig'
import TeamBilling from './pages/TeamBilling'
const WorkspaceApprovals = React.lazy(() => import('./pages/WorkspaceApprovals'))

const queryClient = new QueryClient()


function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Router>
          <AuthProvider>
            <div className="min-h-screen bg-background">
              <Toaster />
              <Sonner position="bottom-right" />
              <Routes>
                {/* Root route with authentication-based redirect */}
                <Route path="/" element={<RootRedirect />} />
                
                {/* Public routes */}
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/auth/login" element={<LoginForm />} />
                <Route path="/auth/signup" element={<SignupForm />} />

                {/* Internal reviewers authenticate independently of customer team/billing gates. */}
                <Route path="/internal/workspace-approvals/:teamId?" element={
                  <ProtectedRoute>
                    <React.Suspense fallback={<p role="status" className="p-6">Loading workspace approvals…</p>}>
                      <WorkspaceApprovals />
                    </React.Suspense>
                  </ProtectedRoute>
                } />
                
                {/* Slack OAuth callback route */}
                <Route path="/slack/oauth" element={<SlackOAuthCallback />} />

                {/* MCP OAuth consent page */}
                <Route path="/oauth/consent" element={<OAuthConsent />} />
                
                {/* New UI Demo Route */}
                <Route path="/demo" element={<Index />} />
                
                {/* Protected routes */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <DashboardHome />
                    </ProtectedRoute>
                  }
                />
                
                {/* Claude Code auto check-in install page */}
                <Route
                  path="/install/claude-code"
                  element={
                    <ProtectedRoute>
                      <InstallClaudeCode />
                    </ProtectedRoute>
                  }
                />

                {/* Team-specific protected routes */}
                <Route
                  path="/dashboard/team/:teamId"
                  element={
                    <ProtectedRoute>
                      <TeamDashboardWrapper />
                    </ProtectedRoute>
                  }
                />

                {/* Old flat Standups link — redirect into the Check-ins sub-page now
                    that Standups is a collapsible parent (Check-ins/Summary). */}
                <Route
                  path="/dashboard/team/:teamId/standups"
                  element={<RedirectToCheckins />}
                />

                <Route
                  path="/dashboard/team/:teamId/standups/check-ins"
                  element={
                    <ProtectedRoute>
                      <TeamCheckinsWrapper />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/dashboard/team/:teamId/standups/summary"
                  element={
                    <ProtectedRoute>
                      <TeamStandupSummaryWrapper />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/dashboard/team/:teamId/standups/:userId"
                  element={
                    <ProtectedRoute>
                      <UserStandupHistoryWrapper />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/dashboard/team/:teamId/connectors"
                  element={
                    <ProtectedRoute>
                      <TeamAgentsWrapper />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/dashboard/team/:teamId/connectors/:agentId"
                  element={
                    <ProtectedRoute>
                      <AgentDetailWrapper />
                    </ProtectedRoute>
                  }
                />

                {/* Preserve old bookmarks and notification links after the rename. */}
                <Route
                  path="/dashboard/team/:teamId/agents"
                  element={<RedirectToConnectors />}
                />

                <Route
                  path="/dashboard/team/:teamId/agents/:agentId"
                  element={<RedirectToConnectors />}
                />

                <Route
                  path="/dashboard/team/:teamId/team-management"
                  element={
                    <ProtectedRoute>
                      <TeamManagementWrapper />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/dashboard/team/:teamId/projects"
                  element={
                    <ProtectedRoute>
                      <ProjectsWrapper />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/dashboard/team/:teamId/projects/:projectId"
                  element={
                    <ProtectedRoute>
                      <ProjectDetailsWrapper />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/dashboard/team/:teamId/config"
                  element={
                    <ProtectedRoute>
                      <TeamConfigWrapper />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/dashboard/team/:teamId/alerts"
                  element={
                    <ProtectedRoute>
                      <TeamConfigWrapper view="alerts" />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/dashboard/team/:teamId/billing"
                  element={
                    <ProtectedRoute>
                      <TeamBillingWrapper />
                    </ProtectedRoute>
                  }
                />

                {/* Legacy routes - keeping for backward compatibility */}
                <Route path="/dashboard/:teamId" element={<Dashboard />} />
                <Route path="/install-success" element={<InstallSuccess />} />
                <Route path="/oauth-test" element={<OAuthTest />} />
                <Route path="/voice-record" element={<VoiceRecord />} />
              </Routes>
            </div>
          </AuthProvider>
        </Router>
      </TooltipProvider>
    </QueryClientProvider>
  )
}

// Wrapper component for team dashboard with proper protection
const TeamDashboardWrapper: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>()
  
  if (!teamId) {
    return <Navigate to="/dashboard" replace />
  }
  
  return (
    <TeamProtectedRoute teamId={teamId}>
      <TeamDashboard teamId={teamId} />
    </TeamProtectedRoute>
  )
}

// Redirects the old flat /standups link to its new default child route.
const RedirectToCheckins: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>()

  if (!teamId) {
    return <Navigate to="/dashboard" replace />
  }

  return <Navigate to={`/dashboard/team/${teamId}/standups/check-ins`} replace />
}

const RedirectToConnectors: React.FC = () => {
  const { teamId, agentId } = useParams<{ teamId: string; agentId?: string }>()
  const location = useLocation()

  if (!teamId) {
    return <Navigate to="/dashboard" replace />
  }

  const detailPath = agentId ? `/${agentId}` : ''
  return (
    <Navigate
      to={`/dashboard/team/${teamId}/connectors${detailPath}${location.search}`}
      replace
    />
  )
}

// Wrapper component for the Check-ins page with the same team protection
const TeamCheckinsWrapper: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>()

  if (!teamId) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <TeamProtectedRoute teamId={teamId}>
      <TeamCheckins teamId={teamId} />
    </TeamProtectedRoute>
  )
}

// Wrapper component for the Summary page with the same team protection
const TeamStandupSummaryWrapper: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>()

  if (!teamId) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <TeamProtectedRoute teamId={teamId} requiredRole="manager">
      <TeamStandupSummary teamId={teamId} />
    </TeamProtectedRoute>
  )
}

// Wrapper component for the agents page with the same team protection
const TeamAgentsWrapper: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>()

  if (!teamId) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <TeamProtectedRoute teamId={teamId}>
      <TeamAgents teamId={teamId} />
    </TeamProtectedRoute>
  )
}

// Wrapper component for one agent's detail page with the same team protection
const AgentDetailWrapper: React.FC = () => {
  const { teamId, agentId } = useParams<{ teamId: string; agentId: string }>()

  if (!teamId || !agentId) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <TeamProtectedRoute teamId={teamId}>
      <AgentDetail teamId={teamId} agentId={agentId} />
    </TeamProtectedRoute>
  )
}

// Wrapper component for the new Team Management page (replaces the old
// TeamManagement/TeamSettings.tsx modal)
const TeamManagementWrapper: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>()

  if (!teamId) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <TeamProtectedRoute teamId={teamId} requiredRole="admin">
      <TeamManagement teamId={teamId} />
    </TeamProtectedRoute>
  )
}

// Wrapper component for the Projects page
const ProjectsWrapper: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>()

  if (!teamId) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <TeamProtectedRoute teamId={teamId} requiredRole="manager">
      <Projects teamId={teamId} />
    </TeamProtectedRoute>
  )
}

// Wrapper component for one project's full-page details view.
const ProjectDetailsWrapper: React.FC = () => {
  const { teamId, projectId } = useParams<{ teamId: string; projectId: string }>()

  if (!teamId || !projectId) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <TeamProtectedRoute teamId={teamId} requiredRole="manager">
      <ProjectDetails teamId={teamId} projectId={projectId} />
    </TeamProtectedRoute>
  )
}

// Wrapper component for the Config page (Standup Config, split out of the
// old Team Management modal's tabs)
const TeamConfigWrapper: React.FC<{ view?: 'config' | 'alerts' }> = ({ view = 'config' }) => {
  const { teamId } = useParams<{ teamId: string }>()

  if (!teamId) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <TeamProtectedRoute teamId={teamId} requiredRole="manager">
      <TeamConfig key={`${teamId}:${view}`} teamId={teamId} view={view} />
    </TeamProtectedRoute>
  )
}

// Wrapper component for the Billing page (split out of the old Team
// Management modal's tabs)
const TeamBillingWrapper: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>()

  if (!teamId) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <TeamProtectedRoute teamId={teamId} requiredRole="admin">
      <TeamBilling teamId={teamId} />
    </TeamProtectedRoute>
  )
}

// Wrapper component for one person's standup history page
const UserStandupHistoryWrapper: React.FC = () => {
  const { teamId, userId } = useParams<{ teamId: string; userId: string }>()

  if (!teamId || !userId) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <TeamProtectedRoute teamId={teamId}>
      <UserStandupHistory teamId={teamId} userId={userId} />
    </TeamProtectedRoute>
  )
}

export default App
